import clsx from 'clsx';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  createContext,
  forwardRef,
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo,
} from 'react';

import { useId } from '@/react/hooks/useId';

import { Icon, IconType } from '@@/Icon';

const cardContainer = cva(
  [
    'border border-solid border-gray-5',
    'th-highcontrast:border-white',
    'th-dark:border-legacy-grey-3',
  ],
  {
    variants: {
      variant: {
        default: [
          'rounded-xl bg-white',
          'th-highcontrast:bg-black',
          'th-dark:bg-gray-iron-11',
        ],
        filled: [
          'rounded-lg bg-gray-neutral-3',
          'th-highcontrast:bg-gray-warm-10',
          'th-dark:bg-gray-iron-10',
        ],
      },
      shadow: {
        true: 'shadow-md',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export type CardVariant = NonNullable<
  VariantProps<typeof cardContainer>['variant']
>;

interface CardContextValue {
  variant: CardVariant;
  titleId: string | undefined;
}

const CardContext = createContext<CardContextValue>({
  variant: 'default',
  titleId: undefined,
});

interface CardContainerProps extends VariantProps<typeof cardContainer> {
  className?: string;
  'aria-label'?: string;
}

const CardContainer = forwardRef<
  HTMLElement,
  PropsWithChildren<CardContainerProps>
>(function CardContainer(
  { className, children, shadow, variant, 'aria-label': ariaLabel },
  ref
) {
  const generatedId = useId();
  const titleId = ariaLabel ? undefined : `card-title-${generatedId}`;
  const contextValue = useMemo(
    () => ({ variant: variant ?? 'default', titleId }),
    [variant, titleId]
  );

  return (
    <CardContext.Provider value={contextValue}>
      <section
        ref={ref}
        aria-label={ariaLabel}
        aria-labelledby={titleId}
        className={cardContainer({ variant, shadow, className })}
      >
        {children}
      </section>
    </CardContext.Provider>
  );
});

interface CardBodyProps {
  className?: string;
}

function CardBody({ className, children }: PropsWithChildren<CardBodyProps>) {
  return <div className={clsx('p-5', className)}>{children}</div>;
}

interface CardHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: IconType;
  actions?: ReactNode;
}

function CardHeader({ title, subtitle, icon, actions }: CardHeaderProps) {
  const { variant, titleId } = useContext(CardContext);

  return (
    <div
      className={clsx(
        'flex min-h-14 items-center justify-between gap-4 px-4 py-3.5',
        variant === 'filled' ? 'rounded-t-lg' : 'rounded-t-xl',
        'border-0 border-b border-solid border-gray-5 bg-gray-iron-2',
        'th-dark:border-legacy-grey-3 th-dark:bg-gray-iron-10',
        'th-highcontrast:border-white th-highcontrast:bg-gray-warm-10'
      )}
    >
      <div className="flex flex-col gap-1">
        <div className="flex min-h-5 items-center gap-2">
          {icon && (
            <Icon
              icon={icon}
              size="md"
              className="shrink-0 opacity-70 th-highcontrast:text-white th-dark:text-gray-3"
            />
          )}
          <span
            id={titleId}
            className="text-sm leading-tight font-extrabold tracking-wide text-gray-9 th-highcontrast:text-white th-dark:text-white"
          >
            {title}
          </span>
        </div>
        {subtitle && (
          <span className="text-xs leading-tight text-[var(--text-muted-color)]">
            {subtitle}
          </span>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

/**
 * Low-level card surface. Compose with `Card.Header` for a titled section and `Card.Body` for
 * padded content. Use `variant="filled"` for a gray background.
 * Reach for `Widget` when you need a full dashboard panel with toolbar.
 *
 * @example
 * <Card.Container>
 *   <Card.Header title="Container registries" subtitle="2 connected" />
 *   <Card.Body>Manage pull-through caching and credentials.</Card.Body>
 * </Card.Container>
 */
export const Card = {
  Container: CardContainer,
  Header: CardHeader,
  Body: CardBody,
};
